import {InputText} from '@components/commons/input-text'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {AssignmentsURLParams, TableCounters} from '@features/home/types.ts'
import {cn, debounce} from '@utilities/helpers.ts'
import {ChangeEvent, ReactNode, useRef, useState} from 'react'
import {SearchLgIcon, SwitchVertical01Icon, XCloseIcon} from '@components/ui/icon'
import {useTranslation} from '@/translations/i18n.tsx'
import {Container} from '@components/ui/container'
import {ASSIGNMENTS_FILTERS} from '@features/home/utils.tsx'
import {Button} from '@components/ui/button'
import {CoordinationTable} from '@features/home/components/coordination-table'
import {PendingSubmissionTable} from '@features/home/components/pending-submission-table'
import {NeedMoreAssetsTable} from '@features/home/components/need-more-assets-table'
import {UpcomingTable} from '@features/home/components/upcoming-table'
import {CompletedTable} from '@features/home/components/completed-table'
import {useTableCounts} from '@features/home/queries/useTableCounts.ts'
import {Skeleton} from '@components/ui/skeleton'
import {TableSortingMobileModal} from '@features/home/components/table-sorting-mobile-modal'
import {useMediaQuery} from '@hooks/use-media-query'

const searchParamsToTable = (onClearSearch: () => void) =>
    ({
        coordination: <CoordinationTable onClearSearch={onClearSearch} />,
        pending_submission: <PendingSubmissionTable onClearSearch={onClearSearch} />,
        needs_more_assets: <NeedMoreAssetsTable onClearSearch={onClearSearch} />,
        upcoming: <UpcomingTable onClearSearch={onClearSearch} />,
        completed: <CompletedTable onClearSearch={onClearSearch} />
    }) as const satisfies Record<AssignmentsURLParams['filter_value'], ReactNode>

const searchParamsToCounters = (counters: TableCounters) =>
    ({
        coordination: counters.coordination_assignments_count,
        pending_submission: counters.pending_submission_assignments_count,
        needs_more_assets: counters.needs_more_assets_assignments_count,
        upcoming: counters.upcoming_assignments_count,
        completed: counters.completed_assignments_count
    }) as const satisfies Record<AssignmentsURLParams['filter_value'], number>

export const YourAssignments = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const [sowSearch, setSowSearch] = useState(false)
    const [sowMobileSortingModal, setSowMobileSortingModal] = useState(false)
    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined})
    }, 500)
    const searchRef = useRef<HTMLInputElement>(null)
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const tableCounts = useTableCounts({search: searchParams.search})

    const onResetSearch = () => {
        if (searchRef.current) {
            searchRef.current.value = ''
        }
        setSearchParams({search: undefined})
    }
    return (
        <div className="flex flex-col gap-6 w-full">
            <div className="flex flex-col gap-6 w-full py-2 sticky top-0 bg-white md:static md:top-auto md:bg-transparent">
                <Container>
                    <div className="flex justify-between w-full items-center">
                        {!isLarge ? (
                            sowSearch ? (
                                <div className="flex gap-4 w-full items-center">
                                    <InputText
                                        className="w-full"
                                        ref={searchRef}
                                        placeholder={t('home:search_placeholder')}
                                        startSlot={<SearchLgIcon size={20} className="text-neutral-700" />}
                                        shape={'rounded'}
                                        inputSize={'sm'}
                                        onChange={onSearch}
                                        type={'text'}
                                        defaultValue={searchParams.search}
                                        variant={'secondary'}
                                        endSlot={
                                            <Button
                                                size={'sm'}
                                                onClick={onResetSearch}
                                                shape={'square'}
                                                variant={'tertiary'}
                                            >
                                                <XCloseIcon />
                                            </Button>
                                        }
                                    />

                                    <Button
                                        onClick={() => setSowSearch(false)}
                                        style={{flexShrink: 0}}
                                        variant={'tertiary'}
                                        shape={'text'}
                                    >
                                        {t('commons:cancel')}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-display-xs text-neutral-900 font-medium">
                                        {t('home:your_assignments')}
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant={'tertiary'}
                                            size={'sm'}
                                            shape={'square'}
                                            onClick={() => setSowMobileSortingModal(true)}
                                        >
                                            <SwitchVertical01Icon />
                                        </Button>
                                        <Button
                                            className="rounded-[20px] bg-neutral-100"
                                            shape={'square'}
                                            variant={'ghost'}
                                            onClick={() => setSowSearch(true)}
                                        >
                                            <SearchLgIcon size={20} className="text-neutral-700" />
                                        </Button>
                                    </div>
                                </>
                            )
                        ) : (
                            <>
                                <h2 className="text-display-xs text-neutral-900 font-medium">
                                    {t('home:your_assignments')}
                                </h2>
                                <div className="w-80">
                                    <InputText
                                        ref={searchRef}
                                        placeholder={t('home:search_placeholder')}
                                        startSlot={<SearchLgIcon size={20} className="text-neutral-700" />}
                                        shape={'rounded'}
                                        inputSize={'sm'}
                                        onChange={onSearch}
                                        type={'text'}
                                        defaultValue={searchParams.search}
                                        variant={'secondary'}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </Container>
                <div className="w-full flex gap-2 px-4 max-w-[1280px] mx-0 overflow-x-scroll lg:px-8 lg:mx-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none">
                    {ASSIGNMENTS_FILTERS.map(value => (
                        <div
                            key={value.id}
                            className={cn(
                                'flex gap-1 shrink-0 border border-neutral-300 py-2 px-4 rounded-[18px] text-sm text-neutral-600 cursor-pointer transition-all duration-200 ease-in-out [&_p]:text-neutral-900 [&_p]:font-medium',
                                searchParams.filter_value == value.id &&
                                    'border-neutral-600 shadow-[var(--shadow-xs),inset_0px_0px_0px_1px_var(--color-neutral-600)]'
                            )}
                            onClick={() =>
                                setSearchParams({
                                    filter_value: value.id,
                                    page: 1,
                                    order_by: undefined,
                                    order_direction: undefined,
                                    hide_cancelled: undefined
                                })
                            }
                        >
                            <p>{t(value.name)}</p>
                            {tableCounts.isPending ? (
                                <Skeleton height={18} width={10} />
                            ) : (
                                tableCounts.data && searchParamsToCounters(tableCounts.data)[value.id]
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <Container>{searchParamsToTable(onResetSearch)[searchParams.filter_value]}</Container>
            {sowMobileSortingModal && <TableSortingMobileModal onClose={() => setSowMobileSortingModal(false)} />}
        </div>
    )
}
